import {useMemo, useState} from "react";
import {Empty, Input, Tag} from "antd";
import {FontAwesomeIcon, PageHeader} from "~/components";
import {GLOSSARY, GLOSSARY_COUNT} from "../glossaryData";
import style from "./GlossaryPage.module.scss";

/** Bỏ dấu tiếng Việt + thường hoá để tìm kiếm không phân biệt dấu. */
function normalize(str) {
	return (str || '')
		.toLowerCase()
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.replace(/đ/g, 'd');
}

/**
 * Trang "Thuật ngữ SEO" (/glossary): từ điển giải thích mọi thuật ngữ cho người
 * dùng mới (Volume, Impressions, CTR, Backlink, LCP...). Data-driven từ
 * glossaryData.js — chia nhóm, có ô tìm kiếm (bỏ dấu) + lọc theo nhóm.
 * Ai đăng nhập cũng xem được (không cần quyền).
 */
function GlossaryPage() {

	const [q, setQ] = useState('');
	const [active, setActive] = useState('all');

	const nq = normalize(q.trim());

	// Lọc theo từ khoá tìm kiếm (khớp tên/định nghĩa/alias) + nhóm đang chọn.
	const groups = useMemo(() => {
		return GLOSSARY
			.filter((g) => active === 'all' || g.key === active)
			.map((g) => {
				const terms = !nq
					? g.terms
					: g.terms.filter((t) => {
						const hay = normalize([t.term, t.full, t.short, t.detail, t.example, t.aliases].join(' '));
						return hay.includes(nq);
					});
				return {...g, terms};
			})
			.filter((g) => g.terms.length > 0);
	}, [nq, active]);

	const totalFound = groups.reduce((n, g) => n + g.terms.length, 0);

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-book-open-cover"
				title="Thuật ngữ SEO"
				subtitle={`Giải thích ${GLOSSARY_COUNT} thuật ngữ thường gặp cho người mới — Volume, Impressions, CTR, Backlink, Core Web Vitals...`}
			/>

			<div className="app-card" style={{padding: 16, marginBottom: 16}}>
				<Input
					allowClear
					size="large"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					placeholder="Tìm thuật ngữ... (vd: volume, ctr, backlink, lcp)"
					prefix={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" style={{color: '#9ca3af'}} />}
				/>

				<div className={style.filters}>
					<button
						type="button"
						className={active === 'all' ? `${style.chip} ${style.chipActive}` : style.chip}
						onClick={() => setActive('all')}
					>
						Tất cả
					</button>
					{GLOSSARY.map((g) => (
						<button
							key={g.key}
							type="button"
							className={active === g.key ? `${style.chip} ${style.chipActive}` : style.chip}
							onClick={() => setActive(g.key)}
						>
							<FontAwesomeIcon icon={g.icon} /> {g.label}
						</button>
					))}
				</div>

				{nq && (
					<p style={{margin: '10px 2px 0', color: '#6b7280', fontSize: 13}}>
						Tìm thấy <b>{totalFound}</b> thuật ngữ cho "{q.trim()}".
					</p>
				)}
			</div>

			{groups.length === 0 ? (
				<div className="app-card" style={{padding: 40}}>
					<Empty description={`Không tìm thấy thuật ngữ nào khớp "${q.trim()}"`} />
				</div>
			) : (
				groups.map((g) => (
					<section key={g.key} className={style.group}>
						<div className={style.groupHead}>
							<div className={style.groupIcon}><FontAwesomeIcon icon={g.icon} /></div>
							<div>
								<h2 className={style.groupTitle}>
									{g.label}
									<span className={style.groupCount}>{g.terms.length}</span>
								</h2>
								<p className={style.groupDesc}>{g.desc}</p>
							</div>
						</div>

						<div className={style.grid}>
							{g.terms.map((t) => (
								<article key={t.term} className={`app-card ${style.card}`}>
									<h3 className={style.term}>{t.term}</h3>
									{t.full && <div className={style.full}>{t.full}</div>}
									<p className={style.short}>{t.short}</p>
									{t.detail && <p className={style.detail}>{t.detail}</p>}
									{t.example && (
										<div className={style.example}>
											<Tag color="blue" bordered={false}>Ví dụ</Tag>
											<span>{t.example}</span>
										</div>
									)}
								</article>
							))}
						</div>
					</section>
				))
			)}
		</div>
	);
}

export default GlossaryPage;
